import { useEffect, useState, useCallback } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { JiraProject, JiraProjects } from '../__types__/apiUtils';
// TODO: move to __types__/apiUtils.ts or create a new file for single issue create form types
import type { IssueType } from '../providers/BulkCreateContextProvider';

import { useGetAvailableSitesForSingleIssueCreateForm } from './useGetAvailableSitesForSingleIssueCreateForm';
import { useGetJiraProjectsForSingleIssueCreateForm } from './useGetJiraProjectsForSingleIssueCreateForm';
import { useGetIssueTypesForSingleIssueCreateForm } from './useGetIssueTypesForSingleIssueCreateForm';
import type { UseGetAvailableSitesResult } from './useGetAvailableSitesForSingleIssueCreateForm';

type SitePickerData = {
	availableSites: AvailableSite[];
	selectedSite: AvailableSite | null;
	isSitePickerLoading: boolean;
	error: Error | null;
};

type ProjectPickerData = {
	selectedProject: JiraProject | null;
	optionsRecentProjects: JiraProjects;
	optionsAllProjects: JiraProjects;
	isProjectPickerLoading: boolean;
	error: Error | null;
};

type IssueTypePickerData = {
	selectedIssueType: IssueType | null;
	optionsIssueTypes: IssueType[];
	isIssueTypePickerLoading: boolean;
	error: Error | null;
};

export type SingleIssueCreateInitialData = SitePickerData &
	ProjectPickerData &
	IssueTypePickerData & {
		error: Error | null;
		onSiteChange: (site: AvailableSite | null) => void;
		onProjectChange: (project: JiraProject | null) => void;
		onIssueTypeChange: (issueType: IssueType | null) => void;
	};

export const useGetInitialDataForSingleIssueCreateForm = (): SingleIssueCreateInitialData => {
	// Local state to manage selected values
	const [selectedSite, setSelectedSite] = useState<AvailableSite | null>(null);
	const [selectedProject, setSelectedProject] = useState<JiraProject | null>(null);
	const [projectSelectorQuery, setProjectSelectorQuery] = useState<string | null>(null);

	// Site picker - First step
	const sitesResult = useGetAvailableSitesForSingleIssueCreateForm();
	const {
		availableSites = [],
		isLoading: isSitePickerLoading = false,
		error: sitesError = null,
	} = (sitesResult || {}) as UseGetAvailableSitesResult;

	// Auto-select first site if available
	useEffect(() => {
		if (availableSites.length > 0 && !selectedSite) {
			setSelectedSite(availableSites[0]);
		}
	}, [availableSites, selectedSite]);

	// Project picker - Second step (depends on selected site)
	const {
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject: projectFromHook,
		isLoading: isProjectPickerLoading,
		error: projectsError,
		onProjectChange: handleProjectChangeFromHook,
	} = useGetJiraProjectsForSingleIssueCreateForm(selectedSite, projectSelectorQuery);

	// Update selected project when project hook returns a selection
	useEffect(() => {
		if (projectFromHook) {
			setSelectedProject(projectFromHook);
		}
	}, [projectFromHook]);

	// Issue type picker - Third step (depends on selected site and project)
	const {
		optionsIssueTypes,
		selectedIssueType,
		isLoading: isIssueTypePickerLoading,
		error: issueTypesError,
		onIssueTypeChange,
	} = useGetIssueTypesForSingleIssueCreateForm(selectedSite, selectedProject);

	// Handlers for form changes
	const handleSiteChange = useCallback((site: AvailableSite | null) => {
		setSelectedSite(site);
		// Reset dependent fields
		setSelectedProject(null);
		setProjectSelectorQuery(null);
	}, []);

	const handleProjectChange = useCallback(
		(project: JiraProject | null) => {
			setSelectedProject(project);
			handleProjectChangeFromHook(project);
		},
		[handleProjectChangeFromHook],
	);

	// Combine all data
	const singleIssueCreateInitialData: SingleIssueCreateInitialData = {
		// Site picker data
		availableSites,
		selectedSite,
		isSitePickerLoading,
		// Project picker data
		selectedProject,
		optionsRecentProjects,
		optionsAllProjects,
		isProjectPickerLoading,
		// Issue type picker data
		selectedIssueType,
		optionsIssueTypes,
		isIssueTypePickerLoading,
		// Error handling
		error: sitesError || projectsError || issueTypesError,
		// Change handlers
		onSiteChange: handleSiteChange,
		onProjectChange: handleProjectChange,
		onIssueTypeChange,
	};

	return singleIssueCreateInitialData;
};
